import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';
import { Card } from 'reactstrap';

import CallexLoader from '../../components/CallexLoader';
import Pagination from '../../components/form/Pagination';
import AdminHeader from '../../components/header/AdminHeader';
import { convertToMask, fileDownload } from '../../utils/common';
import whatsappIcon from '../../assets/images/icons/whatsapp.svg';

import { liveCallHistoryGet } from '../../store/slices/callHistorySlice';
import { useUserDetails } from '../../store/slices/authSlice';
import { useCrmDetails } from '../../store/slices/callingSlice';
import {
  setActiveHelpDetails,
  whatsappAddDetails
} from '../../store/slices/tenantSlices/helpDeskSlice';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from '../../config';

const LiveCallHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const user = useUserDetails();
  const crmDetails = useCrmDetails();
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [csvReportData, setCsvReportData] = useState([]);
  const [csvHeaderData, setCsvHeaderData] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isCsvDownload, setIsCsvDownload] = useState(false);
  const [paginationDetails, setPaginationDetails] = useState({ page: 1, total: 0, limit: 10 });
  const [headerFields, setHeaderFields] = useState({
    default: [],
    crm_fields: []
  });

  const initialValues = {
    page: 1,
    limit: 10
  };

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      const payload = {
        ...values,
        crm_number: crmDetails?.[1]?.crmDetails?.primaryNumber
      };
      const response = await dispatch(liveCallHistoryGet(payload)).unwrap();
      if (response) {
        setHeaderFields({
          ...headerFields,
          default: response?.header?.default || [],
          crm_fields: response?.header?.crm_fields || []
        });
        setReportData(response?.data || []);
        setPaginationDetails({
          ...paginationDetails,
          limit: response.limit,
          page: response.page,
          total: response.total
        });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, setFieldValue } = useFormik({
    initialValues,
    onSubmit
  });

  useEffect(() => {
    onSubmit();
  }, [values?.limit, values?.page]);

  const prepareExportProps = () => {
    const data = csvReportData || [];
    return { headers: csvHeaderData, data, filename: 'Call_hostory.csv' };
  };

  const handleExportCSV = async () => {
    setIsExporting(true);

    try {
      const payload = { ...values, crm_number: crmDetails?.[1]?.crmDetails?.primaryNumber };
      const response = await dispatch(liveCallHistoryGet(payload)).unwrap();
      if (response) {
        const combinedHeaders = [
          ...(response?.header?.default || []),
          ...(response?.header?.crm_fields || [])
        ];

        const headers = combinedHeaders.map((val) => ({ label: val?.label, key: val?.name }));

        setCsvReportData(response?.data || []);
        setCsvHeaderData(headers);
        setIsExporting(false);
        setIsCsvDownload(true);
      }
    } catch (error) {
      setIsExporting(false);
      console.log('Error exporting CSV:', error);
    }
  };

  const addNumber = async (primaryNumber) => {
    try {
      const payload = { from: `91${primaryNumber}@c.us`, name: primaryNumber };
      const response = await dispatch(whatsappAddDetails(payload)).unwrap();
      if (response && response?.message) {
        dispatch(
          setActiveHelpDetails({
            ...response?.data,
            id: response?.data?._id,
            name: response?.data?.from_name,
            activeTab: response?.data?.assignedToId ? 'mine' : 'unassigned'
          })
        );
        navigate('/help-desk');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  return (
    <div>
      <AdminHeader
        title={t('callHistory')}
        isExport={reportData?.length > 0}
        csvProps={prepareExportProps()}
        onExportClick={() => handleExportCSV()}
        setIsCsvDownload={setIsCsvDownload}
        isCsvDownload={isCsvDownload}
        isExporting={isExporting}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.length > 0 ? (
        <Card className="p-0 position-relative">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 220px)' }}>
            <table className="report-list-table">
              <thead>
                <tr className="title">
                  <th colSpan={headerFields?.default?.length}>Default</th>
                </tr>
                <tr>
                  {headerFields?.default?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {reportData?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {headerFields?.default?.map((value, idx) => {
                        if (value?.name === 'recording_file') {
                          return (
                            <td key={idx} className="text-center cursor-pointer">
                              {val[value?.name] ? (
                                <Icon.Download
                                  size={16}
                                  onClick={() => {
                                    const link = `${config.BASE_URL}cdrs_report/download?filename=${val[value?.name]}`;
                                    fileDownload(link);
                                  }}
                                />
                              ) : (
                                '-'
                              )}
                            </td>
                          );
                        }
                        if (value?.name === 'crm_number') {
                          return (
                            <td key={idx}>
                              <div className="d-flex justify-content-between align-items-center">
                                {user?.process_details?.numberMasking === 'true'
                                  ? convertToMask(val[value?.name] || '-')
                                  : val[value?.name] || '-'}{' '}
                                {user?.userWhatsappPermission && (
                                  <img
                                    src={whatsappIcon}
                                    width={16}
                                    height={16}
                                    className="cursor-pointer"
                                    onClick={() => addNumber(val[value?.name])}
                                  />
                                )}
                              </div>
                            </td>
                          );
                        }
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <table className="report-list-table">
              <thead>
                <tr className="title">
                  <th colSpan={headerFields?.crm_fields?.length}>CRM</th>
                </tr>
                <tr>
                  {headerFields?.crm_fields?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {reportData?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {headerFields?.crm_fields?.map((value, idx) => {
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-65px' }}>
            <Pagination
              totalCount={paginationDetails?.total}
              limit={paginationDetails?.limit}
              page={paginationDetails?.page}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
              }}
              onPageClick={(val) => setFieldValue('page', val)}
              lastPageClick={(val) => setFieldValue('page', val)}
              firstPageClick={(val) => setFieldValue('page', val)}
              onRefreshClick={() => onSubmit()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          {t('noDataAvailable')}
        </Card>
      )}
    </div>
  );
};

export default LiveCallHistory;
